import { useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Panel,
  PanelAction,
  PanelBody,
  PanelContent,
  PanelHeader,
  PanelIcon,
  PanelMeta,
  PanelTitle,
} from '@multaqa/ui/components/ui/panel'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { Tag } from '@multaqa/ui/components/ui/tag'
import { Building2Icon, PlusIcon, TagIcon, Trash2Icon, UsersIcon, XIcon } from 'lucide-react'
import { RecordField, RecordSection } from '@/components/record-section'
import { localizedText } from '@/lib/me'
import { type PaymentTermResource, paymentTermLabel } from '@/lib/payment-terms'
import { regionName } from '@/lib/region-name'
import type { CustomerResource, TagResource } from '../api'

type CustomerDetailSectionsProps = {
  customer: CustomerResource
  locale: 'en' | 'ar'
  paymentTerms: PaymentTermResource[]
  availableTags: TagResource[]
  selectedTagId: string
  onAttachTag: (tagId: string) => void
  onDetachTag: (tagId: string) => void
  onRemoveContact: (contactId: string) => void
  onAddContact: () => void
}

export function CustomerDetailSections({
  customer,
  locale,
  paymentTerms,
  availableTags,
  selectedTagId,
  onAttachTag,
  onDetachTag,
  onRemoveContact,
  onAddContact,
}: CustomerDetailSectionsProps) {
  const { t } = useTranslation()
  const paymentTermId =
    'payment_terms_id' in customer && typeof customer.payment_terms_id === 'string'
      ? customer.payment_terms_id
      : null
  const paymentTermName = paymentTermLabel(
    paymentTerms,
    paymentTermId,
    customer.payment_terms,
    locale,
  )

  return (
    <div className="margin-sections section-stack" data-testid="customer-detail-grid">
      <RecordSection
        title={t('customer.details')}
        description={t('customer.detailsDescription')}
        icon={<Building2Icon />}
        testId="customer-detail-information"
      >
        <RecordField label={t('customer.code')} value={customer.code} mono />
        <RecordField
          label={t('customer.type')}
          value={
            customer.type === 'company' ? t('customer.typeCompany') : t('customer.typeIndividual')
          }
        />
        <RecordField label={t('customer.country')} value={regionName(customer.country, locale)} />
        <RecordField label={t('customer.defaultCurrency')} value={customer.default_currency} mono />
        <RecordField
          label={t('customer.taxIdentifier')}
          value={customer.tax_identifier || '—'}
          mono
        />
        <RecordField label={t('customer.paymentTerms')} value={paymentTermName} />
        <RecordField label={t('customer.taxTreatment')} value={customer.tax_treatment || '—'} />
        <RecordField
          label={t('customer.address')}
          value={
            typeof customer.address === 'object' && customer.address
              ? localizedText(customer.address, locale) || '—'
              : customer.address || '—'
          }
        />
      </RecordSection>

      <Panel data-testid="customer-detail-contacts">
        <PanelHeader>
          <PanelIcon>
            <UsersIcon />
          </PanelIcon>
          <div className="min-w-0">
            <PanelTitle role="heading" aria-level={2} className="w-fit">
              {t('customer.contacts')}
            </PanelTitle>
            <PanelMeta className="mt-1">{t('customer.contactsDescription')}</PanelMeta>
          </div>
          <PanelAction>
            <Button variant="outline" size="sm" onClick={onAddContact}>
              <PlusIcon className="me-1 size-4" />
              {t('customer.addContact')}
            </Button>
          </PanelAction>
        </PanelHeader>
        <PanelContent>
          {customer.contacts && customer.contacts.length > 0 ? (
            <div
              className="divide-y divide-border-subtle"
              data-testid="customer-detail-contact-list"
            >
              {customer.contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex flex-wrap items-start justify-between gap-3 px-bleed py-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-body font-semibold">{contact.name}</span>
                      {contact.is_primary ? (
                        <Badge tone="neutral">{t('contact.primary')}</Badge>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-small text-muted-foreground">
                      {contact.role ? <span>{contact.role}</span> : null}
                      {contact.phone ? <span dir="ltr">{contact.phone}</span> : null}
                      {contact.email ? <span dir="ltr">{contact.email}</span> : null}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveContact(contact.id)}
                    aria-label={t('action.delete')}
                  >
                    <Trash2Icon className="size-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-bleed py-4">
              <EmptyState
                title={t('customer.noContacts')}
                description={t('customer.noContactsDescription')}
              />
            </div>
          )}
        </PanelContent>
      </Panel>
      <Panel data-testid="customer-detail-tags">
        <PanelHeader>
          <PanelIcon>
            <TagIcon />
          </PanelIcon>
          <div className="min-w-0">
            <PanelTitle role="heading" aria-level={2} className="w-fit">
              {t('customer.tags')}
            </PanelTitle>
            <PanelMeta className="mt-1">{t('customer.tagsDescription')}</PanelMeta>
          </div>
        </PanelHeader>
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {customer.tags && customer.tags.length > 0 ? (
              customer.tags.map((tag) => (
                <Tag key={tag.id} className="flex items-center gap-1">
                  <span>{tag.name}</span>
                  <button
                    type="button"
                    onClick={() => onDetachTag(tag.id)}
                    className="hover:text-destructive focus:outline-none"
                    aria-label={t('customer.detachTag')}
                  >
                    <XIcon className="size-3" />
                  </button>
                </Tag>
              ))
            ) : (
              <span className="text-small text-muted-foreground">{t('customer.noTags')}</span>
            )}
          </div>

          {availableTags.length > 0 ? (
            <Select value={selectedTagId} onValueChange={onAttachTag}>
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder={t('customer.attachTag')} />
              </SelectTrigger>
              <SelectContent>
                {availableTags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </PanelBody>
      </Panel>
    </div>
  )
}
